/**
 * useFormWorkflowRun
 *
 * Owns the "run an applicable workflow" action for the active form: resolves the
 * applicable workflows, decides whether a workflow needs runtime parameters
 * (opening the parameter sidebar) or can run immediately, and executes it. It is
 * composed by `FormWorkflowActionProvider` so the right-click menu, the toolbar
 * dropdown, and the command palette all share one code path and one mounted
 * `WorkflowParameterSidebar`. Returns the applicable workflows, the click
 * handler, and the sidebar element the provider renders.
 *
 * This hook holds NO store subscription (SYD-2252): it sits inside
 * `FormWorkflowActionProvider`, and proxy-compare reports an untouched snapshot
 * root as changed, so a `useSnapshot` here would re-render the provider on every
 * keystroke on the renders where it read nothing. The sidebar's source field
 * values must nonetheless come from a snapshot rather than the bare proxy; with
 * no `sourceParentPath` given it resolves them off the open form itself, behind
 * `destroyOnHidden`. See `useWorkflowSourceFormVersion` for why the proxy cannot
 * be used.
 *
 * The run goes through `useWorkflowRunner`, whose `onRunningChange` drives the
 * `isCreating` flag this hook is handed: that flag lives on
 * `FormWorkflowActionContext` alongside `useFormCloseAndCreate` and
 * `useFormStateChange`, so the runner's own `isRunning` cannot stand in for it.
 * `runWorkflowFromForm` returns its outcome to the sidebar, which latches its Run
 * button on it (SYD-2442). A run that creates a form closes the sidebar before
 * navigating: leave it open and the new form renders behind the drawer mask, under
 * a banner about the run that left it.
 */

import { useCallback, useState } from 'react';
import type { MessageInstance } from 'antd/es/message/interface';
import type { FormType, Workflow, WorkflowRunAttachments } from '#types';
import { workflowNeedsRuntimeParameters } from '@/workflows/runtime/workflowPredicates';
import { useWorkflowRunner } from '@/workflows/runtime/useWorkflowRunner';
import type { WorkflowRunOutcome } from '@/workflows/runtime/workflowRunOutcome.types';
import { workflowRunErrorOutcome } from '@/workflows/runtime/workflowRunErrorOutcome';
import { workflowRunStoppedOutcome } from '@/workflows/runtime/workflowRunStoppedOutcome';
import WorkflowParameterSidebar from '@/workflows/runtime/WorkflowParameterSidebar';
import { useApplicableWorkflows } from '@/use/useApplicableWorkflows';
import { formActions } from '@/store';

type UseFormWorkflowRunParams = {
  formId: string;
  formType?: FormType['docId'];
  formNumber?: string;
  versionId: string | undefined;
  messageApi: MessageInstance;
  /** Fed to `useWorkflowRunner`'s `onRunningChange`; the flag itself is the provider's. */
  setIsCreating: (creating: boolean) => void;
};

export const useFormWorkflowRun = ({
  formId,
  formType,
  formNumber,
  versionId,
  messageApi,
  setIsCreating,
}: UseFormWorkflowRunParams) => {
  const [workflowModal, setWorkflowModal] = useState<Workflow | null>(null);
  const { workflows: applicableWorkflows } = useApplicableWorkflows({
    triggerType: 'formAction',
    formType,
    enabled: true,
  });
  const { runWorkflow, stopRun, stopPhase } = useWorkflowRunner({
    sourceFormId: formId,
    sourceVersionId: versionId,
    onRunningChange: setIsCreating,
  });

  const runWorkflowFromForm = useCallback(
    async (
      workflow: Workflow,
      params: Record<string, unknown>,
      attachments?: WorkflowRunAttachments
    ): Promise<WorkflowRunOutcome | null> => {
      if (!workflow.docId || !versionId) return null;
      try {
        const result = await runWorkflow({ workflow, userParameters: params, attachments });
        if (!result) return null;
        if (!result.success) {
          const stopped = workflowRunStoppedOutcome(result, workflow);
          messageApi.warning(stopped.text, 10);
          return stopped;
        }

        const apiMessage = result.actions
          ?.find(
            (a) =>
              a.actionType === 'callSignifyApi' &&
              (a.result as Record<string, unknown>)?.message
          )
          ?.result as Record<string, unknown> | undefined;
        const successMessage = apiMessage?.message
          ? String(apiMessage.message)
          : `${workflow.description?.shortLabel || 'Workflow'} completed.`;
        if (apiMessage?.message) {
          messageApi.success(successMessage, 10);
        } else {
          messageApi.success(successMessage);
        }

        if (
          result.newFormId &&
          result.newVersionId &&
          (workflow.actions ?? []).some(
            (a) => a.actionType === 'createForm' && a.navigateToNewForm === true
          )
        ) {
          setWorkflowModal(null);
          formActions.navigateToVersion(result.newFormId, result.newVersionId);
        }

        return {
          status: 'success',
          text: successMessage,
          summary: result.summaries?.find((s) => s.actionType === 'send'),
        };
      } catch (err) {
        const outcome = workflowRunErrorOutcome(err, workflow.description?.shortLabel);
        messageApi.error(outcome.text);
        return outcome;
      }
    },
    [versionId, messageApi, runWorkflow]
  );

  const handleWorkflowClick = useCallback(
    async (workflow: Workflow) => {
      if (!workflow.docId) return;
      if (workflowNeedsRuntimeParameters(workflow)) {
        setWorkflowModal(workflow);
        return;
      }
      await runWorkflowFromForm(workflow, {});
    },
    [runWorkflowFromForm]
  );

  const workflowParameterSidebar = (
      <WorkflowParameterSidebar
        workflow={workflowModal}
        open={workflowModal !== null}
        sourceFormId={formId}
        sourceVersionId={versionId}
        onCancel={() => setWorkflowModal(null)}
        onConfirm={async (params, attachments) => {
          const w = workflowModal;
          if (!w) return null;
          return runWorkflowFromForm(w, params, attachments);
        }}
        onStop={stopRun}
        stopPhase={stopPhase}
        formNumber={formNumber}
        formTitle={formNumber ?? 'Form'}
      />
  );

  return { applicableWorkflows, handleWorkflowClick, workflowParameterSidebar };
};
